/**
 * Part names for group ids (batch 8 T14c): ONLY from the part lookup pages of the CURRENT report owner, i.e. the
 * infinite queries `reportKeys.lookup(owner, 'part', q)` that the report's own selectors already loaded. Turnover and
 * valuation return a part group as an id only and the parts API has no "by ids" read, so nothing is fetched for this:
 * no request per row, no scan of the catalog.
 *
 * The inventory parts caches of the rest of the portal (`queryKeys.inventory.parts`) are NOT read. They carry no
 * owner, and the same part id exists in more than one tenant, so a name found there proves nothing about who may see
 * it. Ownership is not inferred from the id and not from the cache having been cleared by someone else: the owner is
 * in the key that is read. An id with no name stays unresolved (see `Named`).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { reportKeys } from '@/hooks/useInventoryReports';

const OWNER_INDEX = reportKeys.all.length;

/** The `{ pages: [{ items: [{ id, label }] }] }` of an infinite lookup query; anything else yields nothing. */
function collect(data: unknown, into: Map<string, string>): void {
  const pages = (data as { pages?: unknown } | null | undefined)?.pages;
  if (!Array.isArray(pages)) return;
  for (const page of pages) {
    const items = (page as { items?: unknown } | null | undefined)?.items;
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      const { id, label } = (item ?? {}) as { id?: unknown; label?: unknown };
      if (typeof id === 'string' && typeof label === 'string' && label !== '') into.set(id, label);
    }
  }
}

/** Labels of the part lookup pages loaded for exactly this owner. `none` (nobody may read) has no labels. */
export function ownerPartLabels(queryClient: QueryClient, owner: string): Map<string, string> {
  const labels = new Map<string, string>();
  if (owner === 'none') return labels;
  for (const [key, data] of queryClient.getQueriesData({ queryKey: [...reportKeys.all, owner, 'lookup', 'part'] })) {
    if (key[OWNER_INDEX] === owner) collect(data, labels);
  }
  return labels;
}

/** The same, kept current while the owner's part lookups load more pages or are removed. */
export function useOwnerPartLabels(owner: string): Map<string, string> {
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => {
      setVersion((current) => current + 1);
    };
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event.query.queryKey;
      if (key[OWNER_INDEX] === owner && key[OWNER_INDEX + 1] === 'lookup' && key[OWNER_INDEX + 2] === 'part') bump();
    });
    bump();
    return unsubscribe;
  }, [queryClient, owner]);
  // `version` is the cache's change counter: the map is read again whenever a part lookup of this owner changed.
  return useMemo(() => {
    void version;
    return ownerPartLabels(queryClient, owner);
  }, [queryClient, owner, version]);
}
